import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Grid, Text, Select, Spinner } from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  getAiPrechecks, getAiFilters, precheckReason, PAGE_ISSUE,
} from 'api/administrador/monitoring/aiGrading';
import { KpiCard, PeriodPicker, TabIntro, StatusPill } from './common';

const pct = (part, total) => (total ? Math.round((part / total) * 100) : 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function Bars({ title, rows, labelOf, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={5}>
      <Text fontSize="sm" fontWeight="700" mb={4}>{title}</Text>
      {!rows.length && <Text fontSize="sm" color="gray.500">{empty}</Text>}
      <Flex direction="column" gap={3}>
        {rows.map((r) => (
          <Box key={labelOf(r)}>
            <Flex justify="space-between" fontSize="13px" mb={1}>
              <Text color="gray.700">{labelOf(r)}</Text>
              <Text color="gray.600" fontVariantNumeric="tabular-nums">{r.count} · {pct(r.count, total)} %</Text>
            </Flex>
            <Box h="8px" borderRadius="999px" bg="gray.100">
              <Box h="8px" borderRadius="999px" bg="#F59E0B" w={`${(r.count / max) * 100}%`} />
            </Box>
          </Box>
        ))}
      </Flex>
    </Box>
  );
}

const ACT_COLS = '2.4fr 0.8fr 0.8fr 0.8fr 0.9fr';

export default function PrechecksTab({ tick, onOpenSitting }) {
  const [filters, setFilters] = useState({ period: 'today', program: '', subjectId: '' });
  const [options, setOptions] = useState({ programs: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAiFilters().then(setOptions).catch(() => setOptions({ programs: [] }));
  }, []);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      setData(await getAiPrechecks(filters));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando la revisión previa:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { if (tick) load(true); }, [tick]);

  const program = options.programs.find((p) => p.value === filters.program);
  const subjects = program ? program.subjects : options.programs.flatMap((p) => p.subjects.map((s) => ({ ...s, name: `${s.name} · ${p.name}` })));
  const k = data?.kpis;
  const fl = data?.flows;

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" mb={5}>
        <TabIntro title="Revisión previa">
          Antes de enviar, cada PDF o imagen se revisa: que se lea, que las páginas estén completas y derechas y que
          corresponda a la actividad. Aquí ves cuánto se marca, por qué, qué actividades generan más avisos y qué entregas
          se enviaron de todas formas (tras 3 intentos), para afinar la revisión y mandar menos entregas al docente.
        </TabIntro>
        <Flex align="center" justify="space-between" gap={4} px={5} pt={3} pb={5} wrap="wrap">
          <PeriodPicker value={filters.period} onChange={(period) => setFilters({ ...filters, period })} />
          <Flex gap={3} wrap="wrap">
            <Box>
              <Text as="label" htmlFor="pc-program" fontSize="11px" fontWeight="700" letterSpacing=".05em" color="gray.500" textTransform="uppercase">Programa</Text>
              <Select
                id="pc-program" size="md" w="240px" bg="white" mt={1} borderRadius="8px" value={filters.program}
                onChange={(e) => setFilters({ ...filters, program: e.target.value, subjectId: '' })}
              >
                <option value="">Todos los programas</option>
                {options.programs.map((p) => <option key={p.value} value={p.value}>{p.name}</option>)}
              </Select>
            </Box>
            <Box>
              <Text as="label" htmlFor="pc-subject" fontSize="11px" fontWeight="700" letterSpacing=".05em" color="gray.500" textTransform="uppercase">Materia</Text>
              <Select
                id="pc-subject" size="md" w="240px" bg="white" mt={1} borderRadius="8px" value={filters.subjectId}
                onChange={(e) => setFilters({ ...filters, subjectId: e.target.value })}
              >
                <option value="">Todas las materias</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Box>
          </Flex>
        </Flex>
      </Box>

      {loading && !data && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}

      {data && (
        <>
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(5, minmax(0, 1fr))' }} gap={3} mb={5}>
            <KpiCard label="Archivos revisados" value={k.reviewed} foot={`${k.pdfs} PDF · ${k.images} imágenes`} color="#574feb" />
            <KpiCard label="Marcados" value={k.flaggedRate === null ? '—' : `${String(k.flaggedRate).replace('.', ',')} %`} foot={plural(k.flagged, 'archivo difícil de leer', 'archivos difíciles de leer')} color="#F59E0B" />
            <KpiCard label="Corregidos" value={fl.corrected} foot={`de ${plural(fl.flagged, 'entrega con aviso', 'entregas con aviso')}`} color="#10B981" />
            <KpiCard label="Enviados de todas formas" value={fl.overridden} foot={fl.flagged ? `${pct(fl.overridden, fl.flagged)} % de las que tuvieron aviso` : 'van completas al docente'} color="#B45309" />
            <KpiCard
              label="Sin enviar" value={fl.notSent} color="#94A3B8"
              foot={k.unchecked ? `${k.unchecked} sin revisar (falla del servicio)` : k.avgSeconds !== null ? `revisión en ${String(k.avgSeconds).replace('.', ',')} s promedio` : 'tras un aviso'}
            />
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4} mb={5}>
            <Bars title="Motivo del aviso" rows={data.reasons} labelOf={(r) => precheckReason(r.reason)} empty="Ningún archivo marcado en el periodo." />
            <Bars title="Problema por página (PDF)" rows={data.pageIssues} labelOf={(r) => PAGE_ISSUE[r.issue] || r.issue} empty="Sin páginas marcadas en el periodo." />
          </Grid>

          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" mb={5}>
            <Box px={5} pt={4} pb={3}>
              <Text fontSize="sm" fontWeight="700">Actividades con más avisos</Text>
              <Text fontSize="12px" color="gray.500">Si una actividad concentra avisos, revisa su enunciado o las instrucciones de entrega.</Text>
            </Box>
            <Grid templateColumns={ACT_COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
              <Text>Actividad</Text><Text>Revisados</Text><Text>Marcados</Text><Text>% marcado</Text><Text>De todas formas</Text>
            </Grid>
            {!data.activities.length && <Text textAlign="center" py={8} color="gray.500" fontSize="sm">Ninguna actividad con avisos en el periodo.</Text>}
            {data.activities.map((a) => (
              <Grid key={`${a.activity.id}-${a.subject?.id}`} templateColumns={ACT_COLS} px={5} py={3} alignItems="center" borderTop="1px solid" borderColor="gray.100" fontSize="sm">
                <Box minW={0} pr={3}>
                  <Text fontWeight="600" isTruncated>{a.activity.title || `Actividad ${a.activity.id}`}</Text>
                  <Text fontSize="12px" color="gray.500" isTruncated>{a.subject?.name || 'Sin materia'}</Text>
                </Box>
                <Text>{a.reviewed}</Text>
                <Text>{a.flagged}</Text>
                <Text color={a.flaggedRate >= 50 ? '#B45309' : 'gray.700'} fontWeight={a.flaggedRate >= 50 ? '700' : '400'}>{a.flaggedRate} %</Text>
                <Text>{a.overridden}</Text>
              </Grid>
            ))}
          </Box>

          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
            <Box px={5} pt={4} pb={3}>
              <Text fontSize="sm" fontWeight="700">Enviadas de todas formas</Text>
              <Text fontSize="12px" color="gray.500">
                La entrega completa pasó al docente. Ábrela para ver qué se marcó: si el archivo sí se leía, es un aviso de más que conviene afinar.
              </Text>
            </Box>
            {!data.overrides.length && <Text textAlign="center" py={8} color="gray.500" fontSize="sm">Ninguna en el periodo.</Text>}
            {data.overrides.map((o) => (
              <Flex
                key={o.sittingId} as="button" type="button" onClick={() => onOpenSitting(o.sittingId)} w="100%" textAlign="left"
                px={5} py={3} gap={4} align="center" justify="space-between" borderTop="1px solid" borderColor="gray.100"
                _hover={{ bg: 'gray.50' }} _focusVisible={{ outline: '2px solid #574feb', outlineOffset: '-2px' }}
              >
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" isTruncated>{o.activity}</Text>
                  <Text fontSize="12px" color="gray.500" isTruncated>
                    {[o.student, o.subject, dayjs(o.createdAt).format('DD MMM · HH:mm')].filter(Boolean).join(' · ')}
                  </Text>
                  <Text fontSize="12px" color="#B45309" mt={1}>
                    {[
                      ...o.reasons.map(precheckReason),
                      ...o.issues.map((i) => (PAGE_ISSUE[i] || i).toLowerCase()),
                    ].filter(Boolean).join(' · ') || 'Motivo no registrado'}
                    {` · ${plural(o.attempts, 'intento', 'intentos')}`}
                  </Text>
                </Box>
                <StatusPill label={o.graded ? 'Calificada por el docente' : 'Con el docente'} tone={o.graded ? 'purple' : 'gray'} />
              </Flex>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
